import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import * as _ from 'lodash';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UAParser } from 'ua-parser-js';

import { environment } from '@fantaz/shared/app-config';
import {
  BestScores,
  buyPassResponse,
  CasualTournamentDetails,
  CasualTournamentEntity,
  ConfiguredPasses,
  EventSchedule,
  FaceItemModel,
  Game,
  Leaderboard,
  LeaderboardResponse,
  Location,
  PrizeDetails,
  TournamentPass,
  TournamentPasses,
  TournamentsQueryParams,
  TournamentsResponse,
  TournamentTagsEnum,
  TournamentType,
  UserGameBestScores,
  UserPasses,
} from '@fantaz/shared/data-access/models';
import { BlockDeviceService } from '@fantaz/shared/utils/block-device';

import { AuthServiceInjectionToken } from './auth';
import { UtilsService } from './utils.service';

type TournamentSearchParams = {
  limit?: number;
};

function mapToFacet<T>(
  items: T[],
  transformFn: (item: T) => FaceItemModel,
): FaceItemModel[] {
  return items.map((item) => {
    return transformFn(item);
  });
}

function mergeGames(cash: TournamentsResponse, prize: TournamentsResponse) {
  const games = _.unionBy(
    cash.gamesByEventsList,
    prize.gamesByEventsList,
    'key',
  );

  return games;
}

function getGameImageUrl(game: Game): string {
  const result = game.key.split('.');
  const [gameName, version] = result || ['default', 'v1'];

  return `/shared/img/games/${gameName}/${version}/${gameName}-avatar.webp`;
}

const tags: any[] = [
  {
    group: 'type',
    key: 'prize',
    label: 'Prize',
    tooltip: `Win physical items such as electronics, jewelry, furniture, home goods, cookware, or other valuable prizes by competing in these tournaments with paid entries.`,
    image: '/shared/img/tournaments/tournament_types/flat/prize.svg',
  },
  {
    group: 'type',
    key: 'casual',
    label: 'Casual',
    tooltip: `Dynamic tournaments with limited number of seats where the winner is called out as soon as all seats are filled.`,
    image: `/shared/img/tournaments/tournament_types/flat/casual.svg`,
  },
  {
    group: 'tags',
    key: 'cash',
    label: 'Cash prize',
    tooltip: `Play for lucrative cash rewards. Entry fees apply, and winners earn money based on their performance.`,
    image: `/shared/img/tournaments/tournament_types/flat/cash.svg`,
  },
  {
    group: 'tags',
    key: 'zbo',
    label: 'ZBO',
    tooltip: 'Exclusive tournaments only for ZBO members.',
    image: `/shared/img/tournaments/tournament_types/flat/zbo.svg`,
  },
  {
    group: 'tags',
    key: 'free',
    label: 'Free',
    tooltip: `Compete to win exciting prizes without paying an entry fee. Open to ALL players, perfect for beginners and casual gamers.`,
    image: `/shared/img/tournaments/tournament_types/flat/free.svg`,
  },
  {
    group: 'tags',
    key: 'celebrity',
    label: 'Celebrity',
    tooltip: `Access unlimited free play in exclusive ZBO (FantaZ Business Owner) tournaments to claim fantastic prizes.`,
    image: `/shared/img/tournaments/tournament_types/flat/celebrity_event.svg`,
  },
  {
    group: 'tags',
    key: 'charity',
    label: 'Charity',
    tooltip: `Participate to secure extraordinary prizes while supporting those in need through charitable efforts.`,
    image: `/shared/img/tournaments/tournament_types/flat/celebrity_event.svg`,
  },
  {
    group: 'tags',
    key: 'special-discount',
    label: 'Special discount (Pass)',
    tooltip: `Tournaments with discounted passed, save and win.`,
    image: `/shared/img/tournaments/tournament_types/flat/special_discount_pass.svg`,
  },
];

const status: any[] = [
  {
    group: 'status',
    key: 'active',
    label: 'Active',
  },
  {
    group: 'status',
    key: 'ended',
    label: 'Ended',
  },
  {
    group: 'status',
    key: 'coming-soon',
    label: 'Coming Soon',
  },
];

const type = [
  {
    group: 'type',
    key: 'prize',
    label: 'Prize',
    image: '/shared/img/tournaments/tournament_types/flat/prize.svg',
  },
  {
    group: 'type',
    key: 'casual',
    label: 'Casual',
    image: '/shared/img/tournaments/tournament_types/flat/casual.svg',
  },
];

// add compatibility with old API
const backwardComp = {
  tournamentType: {
    all: 'ALL',
    cash: 'CASH',
    casual: 'CASUAL',
    prize: 'PRIZE',
  },
  tournamentTags: {
    prize: 'PRIZE TOURNAMENT',
    casual: 'CASH TOURNAMENT',
    cash: 'CASH PRIZE',
    charity: 'CHARITY',
    celebrity: 'CELEBRITY',
    zbo: 'ZBO',
    free: 'FREE',
    'special-discount': 'special discount',
  },
};

@Injectable({ providedIn: 'root' })
export class TournamentApiService {
  authService = inject(AuthServiceInjectionToken);

  constructor(
    private http: HttpClient,
    private utilsService: UtilsService,
    private blockDevice: BlockDeviceService,
  ) {}

  findPromoteTournament(
    params: TournamentSearchParams = {
      limit: 50,
    },
    options?: {
      tournamentType?: TournamentType;
      retiredEvents?: boolean;
      comingSoonOnly?: boolean;
      active?: boolean;
      gameId?: string;
    },
  ): Observable<CasualTournamentEntity[]> {
    // !TODO: move to URLSearchParams API instead
    const query: any = {};

    if (
      options &&
      options.tournamentType &&
      options.tournamentType === TournamentType.PRIZE
    ) {
      query.gameType = 'lept';
    }

    if (
      options &&
      options.retiredEvents !== undefined &&
      options.retiredEvents !== null
    ) {
      query.retiredEvents = options.retiredEvents;
    }

    if (
      options &&
      options.comingSoonOnly !== undefined &&
      options.comingSoonOnly !== null
    ) {
      query.comingSoonOnly = options.comingSoonOnly;
    }
    if (options && options.active !== undefined && options.active !== null) {
      query.activeTournaments = options.active;
    }
    if (options?.gameId) {
      query.gameId = options.gameId;
    }

    const queryString = Object.keys(query)
      .map((key) => {
        return key + '=' + query[key];
      })
      .join('&');

    return this.http
      .get<CasualTournamentEntity[]>(
        `${environment.apiBaseUrl}/api/public/event-client/games-promote?${queryString}`,
        {
          params,
        },
      )
      .pipe(
        map((tournaments) => {
          //
          return tournaments
            .filter((t) => !this.blockDevice.isBlocked(t.game.blockedDevices))
            .map((tournament) => {
              // add game image
              tournament.game.imageUrl = this.utilsService.getEventImageUrl(
                tournament.game.key,
                '-box-175.png',
              );
              tournament.game.imageCoverUrl =
                this.utilsService.getEventImageUrl(
                  tournament.game.key,
                  '-cover.png',
                );

              if (!tournament.prizeRange) {
                tournament.prizeRange = { min: 1, max: 1 };
              }

              // add entry fee range
              tournament.entryFeeRange = {
                start: tournament.eventSchedules[0].entryAmount,
                end: tournament.eventSchedules[
                  tournament.eventSchedules.length - 1
                ].entryAmount,
              };

              if (options?.tournamentType == TournamentType.PRIZE) {
                if (
                  tournament &&
                  tournament.eventSchedules &&
                  tournament.eventSchedules.length > 0
                ) {
                  const eventScheduler = tournament.eventSchedules.find((e) => {
                    return e.lifeExperience;
                  });

                  if (eventScheduler) {
                    tournament.lifeExperience = true;
                  }
                }
              }

              this.utilsService.getPrizeDetails(tournament);

              // coming soon
              const date = new Date();
              if (tournament.casualGameStartTime) {
                const startTime = new Date(tournament.casualGameStartTime);
                tournament.comingSoon = date.getTime() < startTime.getTime();
              }

              if (tournament.casualGameEndTime) {
                const endTime = new Date(tournament.casualGameEndTime);
                tournament.ended = date.getTime() > endTime.getTime();
              }

              if (tournament.metadata?.tags?.includes('ZBO')) {
                tournament.zboOnly = true;
              } else {
                tournament.zboOnly = false;
              }

              return {
                ...tournament,
                metadata: {
                  ...tournament.metadata,
                  tags:
                    tournament &&
                    tournament.metadata &&
                    tournament.metadata.tags
                      ? (tournament.metadata.tags as unknown as string).split(
                          ',',
                        )
                      : [
                          tournament.lifeExperience
                            ? 'Prize Tournament'
                            : 'Cash Tournament',
                        ], // by default use Prize Tournament
                },
              };
            });
        }),
      );
  }

  /**
   * Fetch all tournaments
   */
  fetchTournaments({
    tournamentType,
    ...rest
  }: TournamentsQueryParams): Observable<TournamentsResponse> {
    if (tournamentType == TournamentType.ALL) {
      return forkJoin({
        cash: this.fetchPromoteTournament({
          tournamentType: TournamentType.CASUAL,
          ...rest,
        }),
        prize: this.fetchPromoteTournament({
          tournamentType: TournamentType.PRIZE,
          ...rest,
        }),
      }).pipe(
        map((result) => ({
          ...result.prize,
          total: Math.max(result.prize.total, result.cash.total),
          items: [...result.prize.items, ...result.cash.items],

          facets: {
            games: mapToFacet<any>(
              mergeGames(result.cash, result.prize),
              (game) => ({
                group: 'games',
                key: game.key,
                label: game.name,
                image: getGameImageUrl(game),
              }),
            ),
            tags,
            status,
            type,
          },
          facetOrder: ['games', 'status', 'type'],
        })),
      );
    }

    return this.fetchPromoteTournament({
      tournamentType,
      ...rest,
    }).pipe(
      map((response) => ({
        ...response,
        filtersSelected: {
          query: '',
          status: ['Active'],
          tags: [],
          games: [],
          entryFee: [],
          prizeValue: [],
        },
        sortBy: ['featured:asc'],
        facets: {
          games: [],
          status: [],
          tags: [],
        },
      })),
    );
  }

  //  Fetch promote tournaments from new endpoint tournaments-promote
  fetchPromoteTournament(
    options: TournamentsQueryParams,
  ): Observable<TournamentsResponse> {
    const endpoint = `${environment.apiBaseUrl}/api/public/event-client/tournaments`;
    let params = new HttpParams();
    const queryParams: TournamentsQueryParams = {
      gameIds: options?.gameIds?.length ? options.gameIds : undefined,
      retiredEvents: options?.retiredEvents,
      comingSoonOnly: options?.comingSoonOnly,
      activeTournaments: options?.activeTournaments,
      active: options?.active,

      // TODO: remove this when old API is removed
      tournamentType:
        (backwardComp.tournamentType[
          options?.tournamentType as keyof typeof backwardComp.tournamentType
        ] as TournamentType) || options?.tournamentType,

      // TODO: remove this when old API is removed
      tournamentTags: options?.tournamentTags?.length
        ? [
            ...options.tournamentTags.map(
              (tag) =>
                (backwardComp.tournamentTags[
                  tag as unknown as keyof typeof backwardComp.tournamentTags
                ] as TournamentTagsEnum) || tag,
            ),
            ...options?.tournamentTags,
          ]
        : undefined,

      entryFeeRanges: options?.entryFeeRanges?.length
        ? options.entryFeeRanges
        : undefined,
      prizeValueRanges: options?.prizeValueRanges?.length
        ? options?.prizeValueRanges
        : undefined,
      getEsRanges: options?.getEsRanges,
      search: options?.search,
      pageIndex: options.pageIndex,
      pageSize: options.pageSize,
      ...(options.sortBy ? { sortBy: options.sortBy } : {}),
    };

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value != null) {
        params = params.append(key, value);
      }
    });

    return this.http.get<TournamentsResponse>(endpoint, { params }).pipe(
      map((response) => {
        let items = response.items;

        if (items?.length === 1 && !items[0].uuid) {
          return response;
        }

        items = items
          .filter((t: CasualTournamentEntity) => {
            if (t.metadata?.internalUseOnly == 'true') {
              return false;
            }
            return true;
          })
          .filter(
            (t: CasualTournamentEntity) =>
              !this.blockDevice.isBlocked(t.game.blockedDevices),
          )
          .map((tournament: CasualTournamentEntity) => {
            tournament.game.imageUrl = this.utilsService.getEventImageUrl(
              tournament.game.key,
              '-box-175.png',
            );
            tournament.game.imageCoverUrl = this.utilsService.getEventImageUrl(
              tournament.game.key,
              '-cover.png',
            );

            if (!tournament.prizeRange) {
              tournament.prizeRange = { min: 1, max: 1 };
            }

            // add entry fee range
            const eventSchedules = tournament.eventSchedules.sort(
              (a, b) => Number(a.entryAmount) - Number(b.entryAmount),
            );
            tournament.entryFeeRange = {
              start: eventSchedules[0].entryAmount,
              end: eventSchedules[tournament.eventSchedules.length - 1]
                .entryAmount,
            };

            if (
              options?.tournamentType === TournamentType.PRIZE ||
              options?.tournamentType === TournamentType.ALL
            ) {
              if (tournament?.eventSchedules?.length) {
                const eventScheduler = tournament.eventSchedules.find((e) => {
                  return e.lifeExperience;
                });

                if (eventScheduler) {
                  tournament.lifeExperience = true;
                }
              }
            }

            this.utilsService.getPrizeDetails(tournament);

            // coming soon
            const date = new Date();
            if (tournament.casualGameStartTime) {
              const startTime = new Date(tournament.casualGameStartTime);
              tournament.comingSoon = date.getTime() < startTime.getTime();
            }

            if (tournament.casualGameEndTime) {
              const endTime = new Date(tournament.casualGameEndTime);
              tournament.ended = date.getTime() > endTime.getTime();
            }

            if (tournament.metadata?.tags?.includes('ZBO')) {
              tournament.zboOnly = true;
            } else {
              tournament.zboOnly = false;
            }

            return {
              ...tournament,
              metadata: {
                ...tournament.metadata,
                tags: tournament?.metadata?.tags
                  ? (tournament.metadata.tags as unknown as string)
                      .split(',')
                      .map((tag) =>
                        tag
                          .toLowerCase()
                          .replace(/(^\w{1})|(\s+\w{1})/g, (letter) =>
                            letter.toUpperCase(),
                          ),
                      )
                  : [
                      tournament.lifeExperience
                        ? 'Prize Tournament'
                        : 'Cash Tournament',
                    ], // by default use Prize Tournament
              },
            };
          });

        return { ...response, items };
      }),
    );
  }

  getLeadership(parameters: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
    username: string;
    pageIndex?: number;
    pageSize?: number;
  }): Observable<LeaderboardResponse> {
    let params = new HttpParams();
    params = params.append('pageSize', parameters.pageSize ?? '10');
    params = params.append('pageIndex', parameters.pageIndex ?? '0');
    return this.http
      .get<LeaderboardResponse>(
        `${environment.apiBaseUrl}/api/public/event-api/eventInstance/${parameters.eventPrototypeUuid}/${parameters.eventScheduleUuid}/leaderboard`,
        { params },
      )
      .pipe(
        map((resp) => {
          resp.leaders.forEach((item, index) => {
            item.id = index;
            if (item.player === parameters.username) {
              item.highlighted = true;
            }
          });
          return resp;
        }),
      );
  }

  getBestScorePlayer(params: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
    globalScore?: boolean;
  }): Observable<BestScores> {
    return this.http.get<BestScores>(
      `${environment.apiBaseUrl}/event-client/api/scores`,
      { params },
    );
  }

  getBestScoreGame(gameKey: string): Observable<UserGameBestScores> {
    return this.http.get<UserGameBestScores>(
      `${environment.apiBaseUrl}/event-client/api/user/scores/${gameKey}`,
    );
  }

  getBestScorePlayerByGame(gameKey: string): Observable<UserGameBestScores> {
    return this.http.get<UserGameBestScores>(
      `${environment.apiBaseUrl}/event-client/api/user/scores/${gameKey}`,
    );
  }

  saveBestScoreGame(
    gameKey: string,
    score: number,
  ): Observable<UserGameBestScores> {
    return this.http.post<UserGameBestScores>(
      `${environment.apiBaseUrl}/event-client/api/user/scores/${gameKey}`,
      { score },
    );
  }

  getTournamentDetails(url: string): Observable<any> {
    return this.http
      .get<CasualTournamentDetails>(
        `${environment.apiBaseUrl}/api/public/event/gamedisplay/${url}/details`,
      )
      .pipe(
        map((result) => {
          if (!result) {
            return result;
          }

          const date = new Date();
          const comingSoon = result.casualGameStartTime
            ? date.getTime() < new Date(result.casualGameStartTime).getTime()
            : false;

          const zboOnly = result.metadata?.tags?.includes('ZBO') ?? false;

          let endTime: Date | undefined;
          if (typeof result.casualGameEndTime === 'string') {
            endTime = new Date(result.casualGameEndTime);
          } else if (result?.casualGameEndTime) {
            endTime = result.casualGameEndTime;
          }

          let isAvailable = true;
          if (result.isCompleted || result.isForceFinalize) {
            isAvailable = false;
          } else if (!result.isCompleted && endTime) {
            const endDate =
              typeof endTime === 'string' || typeof endTime === 'number'
                ? new Date(endTime)
                : endTime;
            if (endDate.getTime() < Date.now()) {
              isAvailable = false;
            }
          }

          const detailsForEventSchedule = result.detailsForEventSchedule
            .map((schedule) => {
              const firstPrize = schedule.prizeDetails.find(
                (prize: PrizeDetails) => prize.rank == 1,
              );
              const prize =
                schedule?.prizeDetails.length > 0
                  ? Number(schedule.prizeDetails[0].prizeValue)
                  : 0;

              return {
                ...schedule,
                uuid: schedule.eventScheduleUuid,
                firstPrize,
                prize,
                room: result.rooms[schedule.eventScheduleUuid],
              };
            })
            .sort(this.compare);

          const details = {
            ...result,
            isAvailable,
            detailsForEventSchedule,
            game: {
              ...result.game,
              imageUrl: this.utilsService.getEventImageUrl(
                result.game.key,
                '-box-175.png',
              ),
            },
            comingSoon,
            zboOnly,
            endTime,
          };

          this.storeInvitationTournamentUrl(details);

          return details;
        }),
      );
  }

  private storeInvitationTournamentUrl(
    tournamentDetails: CasualTournamentDetails,
  ): void {
    const searchParams = new URLSearchParams(window.location.search);
    const inviteBy = searchParams.get('inviteBy') || '';
    if (inviteBy !== '') {
      const tournamentName = tournamentDetails?.name || '';
      const tournamentUrl = tournamentDetails?.url || '';
      const gameName = tournamentDetails?.game.name || '';
      const currentUrl = new URL(window.location.href);

      const params = currentUrl.searchParams;
      params.delete('inviteBy');
      const redirectTo = currentUrl.toString();
      localStorage.setItem('redirectTo', redirectTo);
      const url = new URL(`/join/${inviteBy}`, window.location.origin);
      url.searchParams.append('tournamentName', tournamentName);
      url.searchParams.append('gameName', gameName);
      url.searchParams.append('tournamentUrl', tournamentUrl);
      url.searchParams.append('redirectTo', redirectTo);
      localStorage.setItem('joinUrl', url.toString());
    }
  }
  private compare(a: EventSchedule, b: EventSchedule) {
    const prizeA = a.firstPrize?.prizeValue
      ? Number(a.firstPrize?.prizeValue + '')
      : 0;
    const prizeB = b.firstPrize?.prizeValue
      ? Number(b.firstPrize?.prizeValue + '')
      : 0;
    if (
      prizeA !== null &&
      prizeA !== undefined &&
      prizeB !== null &&
      prizeB !== undefined
    ) {
      return (
        a.entryAmount - b.entryAmount ||
        a.totalEntryCount - b.totalEntryCount ||
        prizeA - prizeB
      );
    }
    return 0;
  }

  getGameRating(params: { url: string; userId?: string }): Observable<any> {
    return this.http.get<CasualTournamentDetails>(
      `${environment.apiBaseUrl}/api/public/event/gamedisplay/${params.url}/rating/${params.userId}`,
    );
  }

  getSubscriptionStatus(params: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
    email: string;
  }): Observable<{ status: boolean }> {
    const parsedEmail = params.email.replace('+', '%2B');
    return this.http.get<{ status: boolean }>(
      `${environment.apiBaseUrl}/event-client/api/coming-soon/${params.eventPrototypeUuid}/${params.eventScheduleUuid}?email=${parsedEmail}`,
    );
  }

  getTournamentPasses(params: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
  }): Observable<TournamentPass[]> {
    return this.authService.isLoggedIn().pipe(
      switchMap((isLoggedIn) => {
        const endpoint = `${environment.apiBaseUrl}/event-client/${
          isLoggedIn ? 'auth/' : ''
        }api/passes/${params.eventPrototypeUuid}/${params.eventScheduleUuid}`;
        return this.http.get<TournamentPasses>(endpoint);
      }),
      map((passes) => {
        const all: TournamentPass[] = [];

        if (passes.configured.length > 0) {
          all.push(
            ...this.sortPasses(
              passes.configured.map((e) => ({
                ...e,
                disabled: passes.userPasses.length > 0,
              })),
            ),
          );
        }

        if (passes.userPasses.length > 0) {
          all.push(
            ...(passes.userPasses.map((e) => ({
              ...e,
              purchased: true,
            })) as unknown as TournamentPass[]),
          );
        }

        // combine all passes into object by uuid
        const allPasses: Record<string, TournamentPass> = all.reduce(
          (acc: any, cur) => {
            if (acc[cur.uuid]) {
              acc[cur.uuid] = _.values(
                _.merge(_.keyBy(acc[cur.uuid], 'uuid'), _.keyBy(cur, 'uuid')),
              );
            } else {
              acc[cur.uuid] = {
                ...cur,
                disabled: cur.purchaseLimitReached || cur.disabled,
                canBeBuy:
                  passes.userPasses.length === 0 && !cur.purchaseLimitReached,
              };
            }
            return acc;
          },
          {},
        );

        const allMergePasses = Object.keys(allPasses).map(
          (key) => allPasses[key],
        );

        return allMergePasses.filter((p) => {
          // remove duplicated passes caused by user buy
          if (!p.fantazPassUuid) {
            const index = allMergePasses.findIndex(
              (p2) => p2.fantazPassUuid === p.uuid,
            );

            return index === -1;
          }

          return true;
        });
      }),
    );
  }

  getAllUserTournamentPasses(): Observable<TournamentPasses> {
    return this.http.get<TournamentPasses>(
      `${environment.apiBaseUrl}/event-client/api/user/passes`,
    );
  }

  buyTournamentPass(params: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
    fantazPassUuid: string;
    passIndex: number;
    userLocation: {
      deviceGPS?: Location | null | undefined;
      userAllowToUseFallbackLocation?: boolean | undefined;
    };
  }): Observable<UserPasses> {
    return this.http
      .post<buyPassResponse | { error: { code: string } }>(
        `${environment.apiBaseUrl}/event-api/player-pass`,
        params,
      )
      .pipe(
        map((data: buyPassResponse | { error: { code: string } }) => {
          if ('error' in data) {
            throw new Error(data.error.code);
          }
          return {
            ...data.playerPass,
          } as UserPasses;
        }),
        catchError((error) => {
          const allowedErrors = [
            'LOCATION_NOT_ALLOWED',
            'NOT_ENOUGH_BALANCE',
            'WALLET_FROZEN_BY_ADMIN',
            'WALLET_FROZEN_BY_TAX_INFORMATION_REQUIRED',
            'USER_HAS_PASSES_AVAILABLE',
            'TOURNAMENT_NOT_AVAILABLE',
            'INVALID_FANTAZ_PASS',
            'ALREADY_BOUGHT_PASS',
          ];
          if (allowedErrors.includes(error.message)) {
            return throwError(() => error);
          }
          return throwError(() => new Error('UNKNOWN_ERROR'));
        }),
      );
  }

  fetchLeaderboard({ eventInstanceUuid }: { eventInstanceUuid: string }) {
    return this.http
      .get<Leaderboard>(
        `${environment.apiBaseUrl}/event-client/api/eventInstance/${eventInstanceUuid}/leaderboard`,
      )
      .pipe(
        map((leaderboard) => ({
          ...leaderboard,
          results: leaderboard.results
            .map((r) => ({
              ...r,
              userName: leaderboard.users[r.userUuid],
            }))
            .sort((a, b) => a.rank - b.rank),
        })),
      );
  }

  /*
   * TODO: Move for correct service
   *  // !FIXME: SOLID VIOLATION move to separate service like browser.service.ts or something like that
   */
  unsupportedBrowserValidation() {
    if (window.localStorage.getItem('unsupported-browser-closed')) {
      return;
    }

    const currentUrl = window.location.href;

    if (
      !this.isValidBrowser() &&
      !currentUrl.includes('utilities/unsupported-browser')
    ) {
      window.location.href = `/utilities/unsupported-browser?redirectTo=${window.location.href}`;
    }
  }

  // !FIXME: SOLID VIOLATION move to separate service like browser.service.ts or something like that
  private isValidBrowser() {
    const parser = new UAParser();
    const browser =
      parser && parser.getBrowser() && parser.getBrowser().name?.toLowerCase();
    const browserList = [
      'chrome',
      'firefox',
      'safari',
      'mobile safari',
      'edge',
      'chrome webview',
      'facebook',
      'instagram',
      'snapchat',
      'gsa',
    ];

    if (this.isInAppBrowser()) {
      return true;
    }

    const navigator: any = window.navigator;
    if (navigator.brave && navigator.brave.isBrave()) {
      return false;
    }

    const isTiktok = window.navigator.userAgent
      .toLowerCase()
      .includes('bytedance');

    return (browser && browserList.indexOf(browser) > -1) || isTiktok;
  }

  private sortPasses(passes: ConfiguredPasses[]): ConfiguredPasses[] {
    return passes.sort((a, b) => b.uuid.localeCompare(a.uuid));
  }

  // !FIXME: SOLID VIOLATION move to separate service like browser.service.ts or something like that
  isInAppBrowser() {
    const parser = new UAParser();
    const browser =
      parser && parser.getBrowser() && parser.getBrowser().name?.toLowerCase();
    const navigator: any = window.navigator;
    const isTiktok = navigator.userAgent.toLowerCase().includes('bytedance');
    return browser === 'facebook' || browser === 'instagram' || isTiktok;
  }
}
